import { useState } from "react";
import { Clock, ShieldCheck, ShieldOff, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  REVIEW_COPY,
  reviewMedia,
  useIsMediaReviewer,
  type ReviewScope,
  type ReviewState,
} from "@/lib/media-review";

/**
 * The visible mark on any clip that is not yet cleared for its audience, plus
 * the approve / turn-down controls for moderators.
 */
export function MediaReviewBadge({
  state,
  scope,
  id,
  note,
  onDecided,
  className,
  compact = false,
}: {
  state: ReviewState;
  scope: ReviewScope;
  id: string;
  note?: string | null;
  onDecided?: (state: ReviewState) => void;
  className?: string;
  compact?: boolean;
}) {
  const isReviewer = useIsMediaReviewer();
  const [busy, setBusy] = useState<ReviewState | null>(null);
  if (state === "approved") return null;

  const copy = REVIEW_COPY[state];
  const Icon = state === "pending" ? Clock : ShieldOff;

  const decide = async (next: ReviewState) => {
    setBusy(next);
    try {
      await reviewMedia(scope, id, next);
      toast.success(next === "approved" ? "Video approved — it is visible now." : "Video turned down.");
      onDecided?.(next);
    } catch (e: any) {
      toast.error(e?.message ?? "That decision could not be saved.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <span
        title={note || copy.detail}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
          state === "pending"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
            : "bg-destructive/15 text-destructive",
        )}
      >
        <Icon className="h-3.5 w-3.5" />
        {copy.label}
      </span>
      {!compact && <span className="text-xs text-muted-foreground">{note || copy.detail}</span>}
      {isReviewer && (
        <span className="flex items-center gap-1">
          <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); void decide("approved"); }}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium hover:bg-surface"
            >
              {busy === "approved" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Approve
            </button>
          {state !== "rejected" && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); void decide("rejected"); }}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-destructive hover:bg-destructive/10"
            >
              {busy === "rejected" ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              Turn down
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Placeholder shown in place of a player when the viewer is not part of the
 * audience for that clip yet.
 */
export function MediaReviewPlaceholder({ state, className }: { state: ReviewState; className?: string }) {
  const copy = REVIEW_COPY[state];
  const Icon = state === "pending" ? ShieldCheck : ShieldOff;
  return (
    <div className={cn("grid h-full w-full place-items-center bg-muted/60 p-6 text-center", className)}>
      <div className="space-y-1.5">
        <Icon className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium">{copy.label}</p>
        <p className="mx-auto max-w-[26ch] text-xs text-muted-foreground">{copy.detail}</p>
      </div>
    </div>
  );
}
